import { useState, useRef, useEffect } from "preact/hooks";
import type { ComponentChildren } from "preact";

interface AutoCompleteProps<T> {
  placeholder: string;
  label: string;
  /** May return synchronously (client-side index) or a Promise (server-backed, e.g. pieces). */
  onSearch: (query: string) => T[] | Promise<T[]>;
  onSelect: (item: T) => void;
  /** Row content in the dropdown. May be rich — pieces render label + composer + count. */
  renderItem: (item: T) => ComponentChildren;
  /** Plain text for the input once selected; `renderItem` may be JSX, so it can't serve here. */
  itemLabel: (item: T) => string;
  value: string;
  onClear: () => void;
  /** Search on focus with the current (possibly empty) query — used to offer popular
   *  pieces before the user has typed anything, since 33k facets need a starting point. */
  openOnFocus?: boolean;
  /** Debounce keystrokes. Leave at 0 for local indexes; set for network-backed searches. */
  debounceMs?: number;
}

export function AutoComplete<T>({
  placeholder,
  label,
  onSearch,
  onSelect,
  renderItem,
  itemLabel,
  value,
  onClear,
  openOnFocus = false,
  debounceMs = 0,
}: AutoCompleteProps<T>) {
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState<T[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  // Async results can land out of order (a slow "b" after a fast "bee"); only the newest
  // search may write. Also suppresses an in-flight response after select/clear.
  const searchToken = useRef(0);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setQuery(value);
  }, [value]);

  useEffect(() => () => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
  }, []);

  function runSearch(val: string) {
    const token = ++searchToken.current;
    let out: T[] | Promise<T[]>;
    try {
      out = onSearch(val);
    } catch {
      return; // a throwing sync index shouldn't blank the field
    }

    if (!(out instanceof Promise)) {
      setResults(out);
      setIsOpen(out.length > 0);
      setActiveIndex(-1);
      return;
    }

    setLoading(true);
    // Keep the previous rows on screen while the next page is in flight — clearing here
    // makes the list strobe on every keystroke over a network-backed search.
    out
      .then((found) => {
        if (token !== searchToken.current) return;
        setResults(found);
        setIsOpen(found.length > 0);
        setActiveIndex(-1);
      })
      .catch(() => {
        if (token !== searchToken.current) return;
        setResults([]);
        setIsOpen(false);
      })
      .finally(() => {
        if (token === searchToken.current) setLoading(false);
      });
  }

  function scheduleSearch(val: string) {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (debounceMs > 0) {
      debounceTimer.current = setTimeout(() => runSearch(val), debounceMs);
    } else {
      runSearch(val);
    }
  }

  function handleInput(e: Event) {
    const val = (e.target as HTMLInputElement).value;
    setQuery(val);
    if (val.trim().length >= 1 || openOnFocus) {
      scheduleSearch(val);
    } else {
      searchToken.current++; // strand any in-flight response
      setResults([]);
      setIsOpen(false);
    }
  }

  function handleFocus() {
    if (results.length > 0) {
      setIsOpen(true);
    } else if (openOnFocus) {
      scheduleSearch(query);
    }
  }

  function selectItem(item: T) {
    searchToken.current++; // don't let a pending search reopen the list under the selection
    setQuery(itemLabel(item));
    setIsOpen(false);
    setResults([]);
    onSelect(item);
  }

  function handleKeyDown(e: KeyboardEvent) {
    if (!isOpen) return;
    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setActiveIndex((i) => Math.min(i + 1, results.length - 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setActiveIndex((i) => Math.max(i - 1, 0));
        break;
      case "Enter":
        e.preventDefault();
        if (activeIndex >= 0 && activeIndex < results.length) {
          selectItem(results[activeIndex]);
        } else if (results.length > 0) {
          // Type-and-Enter is the universal search gesture: with nothing highlighted,
          // commit the top suggestion instead of silently doing nothing.
          selectItem(results[0]);
        }
        break;
      case "Escape":
        setIsOpen(false);
        break;
    }
  }

  function handleClear() {
    searchToken.current++;
    setQuery("");
    setResults([]);
    setIsOpen(false);
    onClear();
    inputRef.current?.focus();
  }

  return (
    <div class="autocomplete">
      <label class="sr-only" for={`ac-${label}`}>
        {label}
      </label>
      <div class="autocomplete-input-wrap">
        <input
          id={`ac-${label}`}
          ref={inputRef}
          type="text"
          placeholder={placeholder}
          value={query}
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          onFocus={handleFocus}
          onBlur={() => setTimeout(() => setIsOpen(false), 200)}
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          aria-controls={`ac-list-${label}`}
          aria-busy={loading}
          autocomplete="off"
        />
        {loading && <span class="autocomplete-spinner" aria-hidden="true" />}
        {query && (
          <button
            class="autocomplete-clear"
            onClick={handleClear}
            aria-label={`Clear ${label}`}
            type="button"
          >
            &times;
          </button>
        )}
      </div>
      {isOpen && results.length > 0 && (
        <ul
          id={`ac-list-${label}`}
          ref={listRef}
          class="autocomplete-list"
          role="listbox"
        >
          {results.map((item, i) => (
            <li
              key={i}
              role="option"
              aria-selected={i === activeIndex}
              class={i === activeIndex ? "active" : ""}
              // mousedown, not click: the input's blur handler closes the list first.
              onMouseDown={() => selectItem(item)}
            >
              {renderItem(item)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
